import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { Button } from './UI';

// Окна подтверждения и ввода в стиле приложения вместо window.confirm / window.prompt:
// if (!(await confirmDialog('Удалить счёт?'))) return;  const v = await promptDialog('Номер акта', x.number);
export interface ConfirmOptions { title?: string; confirmLabel?: string; cancelLabel?: string; danger?: boolean }
export interface PromptOptions { label?: string; confirmLabel?: string; type?: 'text' | 'date'; placeholder?: string; hint?: string }

type Request = { id: number } & (
    | { kind: 'confirm'; message: string; options: ConfirmOptions; resolve: (v: boolean) => void }
    | { kind: 'prompt'; message: string; initial: string; options: PromptOptions; resolve: (v: string | null) => void });

const queue: Request[] = [];
let notify: (() => void) | null = null;
let lastId = 0;
const push = (r: Request) => { queue.push(r); notify?.(); };

export const confirmDialog = (message: string, options: ConfirmOptions = {}) =>
    new Promise<boolean>(resolve => push({ id: ++lastId, kind: 'confirm', message, options, resolve }));
export const promptDialog = (message: string, initial = '', options: PromptOptions = {}) =>
    new Promise<string | null>(resolve => push({ id: ++lastId, kind: 'prompt', message, initial, options, resolve }));

const DANGER_RE = /^(Удалить|Убрать|Отключить|Отменить)/;
const ACTION_RE = /^(Провести|Создать|Перенести|Сформировать)/;

// Текст вопроса → заголовок (первый вопрос) и пояснение, кнопка — глагол из вопроса
export const describeConfirm = (message: string, o: ConfirmOptions = {}) => {
    const split = /^([\s\S]+?\?)\s+([\s\S]+)$/.exec(message) || /^([^\n]+)\n+([\s\S]+)$/.exec(message);
    const title = o.title || (split ? split[1] : message);
    const body = o.title ? message : split ? split[2] : '';
    const again = /Всё равно (\S+?)[\s?]/.exec(message);
    const verb = (DANGER_RE.exec(message) || ACTION_RE.exec(message) || [])[0];
    const label = verb || (again && `Всё равно ${again[1]}`) || 'Подтвердить';
    return { title, body, danger: o.danger ?? DANGER_RE.test(message), warning: !!again, confirmLabel: o.confirmLabel || label,
             cancelLabel: o.cancelLabel || (verb === 'Отменить' ? 'Не отменять' : 'Отмена') };
};

const Frame: React.FC<{ tone: 'danger' | 'warning' | 'info'; title: string; onCancel: () => void; children: React.ReactNode }> = ({ tone, title, onCancel, children }) => (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}>
        <div role="alertdialog" aria-modal="true" aria-label={title} className="w-full max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto rounded-xl bg-white p-4 sm:p-5 shadow-2xl ring-1 ring-black/5">
            <div className="flex gap-3">
                <span className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${tone === 'danger' ? 'bg-red-50 text-red-600' : tone === 'warning' ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600'}`}>
                    {tone === 'info' ? <HelpCircle size={18} /> : <AlertTriangle size={18} />}
                </span>
                <div className="min-w-0 flex-1">{children}</div>
            </div>
        </div>
    </div>
);

const ConfirmView: React.FC<{ message: string; options: ConfirmOptions; done: (v: boolean) => void }> = ({ message, options, done }) => {
    const d = describeConfirm(message, options);
    return (
        <Frame tone={d.danger ? 'danger' : d.warning ? 'warning' : 'info'} title={d.title} onCancel={() => done(false)}>
            <h3 className="break-words text-base font-semibold text-slate-900 whitespace-pre-line">{d.title}</h3>
            {d.body && <p className="mt-1 break-words text-sm text-slate-600 whitespace-pre-line">{d.body}</p>}
            <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <Button variant="secondary" onClick={() => done(false)}>{d.cancelLabel}</Button>
                <Button variant={d.danger ? 'danger' : 'primary'} autoFocus onClick={() => done(true)}>{d.confirmLabel}</Button>
            </div>
        </Frame>
    );
};

const PromptView: React.FC<{ message: string; initial: string; options: PromptOptions; done: (v: string | null) => void }> = ({ message, initial, options, done }) => {
    const [value, setValue] = useState(initial);
    const input = useRef<HTMLInputElement>(null);
    useEffect(() => { input.current?.focus(); input.current?.select(); }, []);
    return (
        <Frame tone="info" title={message} onCancel={() => done(null)}>
            <form onSubmit={e => { e.preventDefault(); done(value); }}>
                <h3 className="break-words text-base font-semibold text-slate-900">{message}</h3>
                {options.hint && <p className="mt-1 text-sm text-slate-600">{options.hint}</p>}
                <input ref={input} type={options.type || 'text'} value={value} placeholder={options.placeholder} autoComplete="off"
                    onChange={e => setValue(e.target.value)} className="mt-3 block w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <Button type="button" variant="secondary" onClick={() => done(null)}>Отмена</Button>
                    <Button type="submit">{options.confirmLabel || 'Сохранить'}</Button>
                </div>
            </form>
        </Frame>
    );
};

// Один раз в корне приложения. Окна идут по очереди; Esc и клик мимо окна — отмена
export const DialogHost: React.FC = () => {
    const [current, setCurrent] = useState<Request | null>(queue[0] || null);
    useEffect(() => {
        notify = () => setCurrent(c => c || queue[0] || null);
        notify();
        return () => { notify = null; };
    }, []);
    const finish = (value: boolean | string | null) => {
        const r = queue.shift();
        if (r) (r.resolve as (v: boolean | string | null) => void)(value);
        setCurrent(queue[0] || null);
    };
    useEffect(() => {
        if (!current) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            finish(current.kind === 'confirm' ? false : null);
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [current]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!current) return null;
    return current.kind === 'confirm'
        ? <ConfirmView key={current.id} message={current.message} options={current.options} done={finish} />
        : <PromptView key={current.id} message={current.message} initial={current.initial} options={current.options} done={finish} />;
};
